import { useEffect, useState } from "react";
import { Lock, Download, Upload, Loader2, ArrowLeft } from "lucide-react";
import { api, fileUrl } from "./api";
import { useAuth } from "./AuthContext";

export function GalleryPage() {
  const [events, setEvents] = useState(null);
  const [active, setActive] = useState(null);
  useEffect(() => { api.get("/events").then((r) => setEvents(r.data)).catch(() => setEvents([])); }, []);

  if (active) return <EventGallery event={active} onBack={() => setActive(null)} />;

  return (
    <div>
      <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-gold">/ Relive The Night</p>
      <h1 className="mt-3 mb-8 font-oswald text-5xl font-700 uppercase leading-[0.85] tracking-tighter">Gallery</h1>
      {events === null && <p className="text-zinc-500">Loading…</p>}
      {events?.length === 0 && <p className="text-zinc-500">No event galleries yet.</p>}
      <div className="grid gap-4">
        {events?.map((ev) => (
          <button key={ev.id} data-testid={`gallery-event-${ev.id}`} onClick={() => setActive(ev)}
            className="flex items-center justify-between border border-white/10 bg-ink-900/60 p-5 text-left hover:border-gold/40">
            <span className="font-oswald text-xl font-600 uppercase tracking-tight">{ev.title}</span>
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">View Photos</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function EventGallery({ event, onBack }) {
  const { isOrganizer } = useAuth();
  const [data, setData] = useState(null);
  const load = () => api.get(`/events/${event.id}/photos`).then((r) => setData(r.data)).catch(() => setData({ locked: false, photos: [] }));
  useEffect(() => { load(); }, [event.id]);

  return (
    <div>
      <button onClick={onBack} className="mb-4 flex items-center gap-2 text-zinc-400 hover:text-gold"><ArrowLeft size={16} /> <span className="font-mono text-[11px] uppercase tracking-[0.2em]">Back</span></button>
      <h1 className="mb-6 font-oswald text-3xl font-700 uppercase tracking-tighter">{event.title}</h1>

      {isOrganizer && <UploadPhoto eventId={event.id} onDone={load} />}

      {data === null && <p className="text-zinc-500">Loading…</p>}
      {data?.locked && (
        <div className="flex flex-col items-center gap-3 border border-white/10 bg-ink-900/60 py-16 text-center">
          <Lock size={28} className="text-gold" />
          <p className="font-oswald text-2xl uppercase tracking-tight">Photos Locked</p>
          <p className="max-w-xs text-sm text-zinc-400">Photos from this party unlock on the event date. Come back after the night!</p>
        </div>
      )}
      {data && !data.locked && data.photos.length === 0 && <p className="text-zinc-500">No photos posted yet.</p>}
      {data && !data.locked && (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
          {data.photos.map((p) => (
            <a key={p.id} data-testid={`photo-${p.id}`} href={fileUrl(p.storage_path)} target="_blank" rel="noreferrer" download
              className="group relative aspect-square overflow-hidden border border-white/10">
              <img src={fileUrl(p.storage_path)} alt="Event" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
              <span className="absolute bottom-2 right-2 flex h-8 w-8 items-center justify-center bg-ink-950/70 text-gold opacity-0 transition-opacity group-hover:opacity-100"><Download size={14} /></span>
            </a>
          ))}
        </div>
      )}
    </div>
  );
}

function UploadPhoto({ eventId, onDone }) {
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true);
    for (const f of files) {
      const fd = new FormData(); fd.append("file", f);
      try { await api.post(`/events/${eventId}/photos`, fd, { headers: { "Content-Type": "multipart/form-data" } }); } catch {}
    }
    setBusy(false); onDone();
  };
  return (
    <label data-testid="upload-photo" className="mb-6 flex cursor-pointer items-center justify-center gap-2 border border-gold/30 bg-ink-900/40 py-4 font-mono text-[11px] uppercase tracking-[0.25em] text-gold">
      {busy ? <Loader2 size={15} className="animate-spin" /> : <><Upload size={15} /> Upload Photos</>}
      <input type="file" accept="image/*" multiple className="hidden" onChange={upload} disabled={busy} />
    </label>
  );
}
